import Vue from "vue";
import { mapMutations, mapState } from "vuex";

import Alert from "sweetalert2";

import { Component } from "@/decorators";
import UsuarioModel from "@/models/usuarioModel";
import ConfiguracaoWhatsAppService from "@/services/atendimentos/configuracaoWhatsAppService";
import NotificationService from "@/services/notificationService";
import MensagemAtendimentoHub from "@/services/websocket/mensagemAtendimentoHub";
import PharmUPHub from "@/services/websocket/pharmUPHub";
import { AppState, Mutations, SessionActions } from "@/store/store";
import Bus from "@/utils/events/bus";

import navMenuComponent from "../navmenu/navmenu.vue";
import notificationComponent from "../notification/view.vue";
import topMenuComponent from "../topmenu/topmenu.vue";

import "./app.scss";

@Component({
    components: {
        topMenuComponent,
        navMenuComponent,
        notificationComponent,
    },
    computed: mapState({
        franquiaCidade: (state: AppState) => state.session.usuarioLogado.franquiaCidade,
        usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        logado: (state: AppState) => state.session.logado,
    }),
    methods: mapMutations(["CLEAR_LISTSPACK", "SET_UNREADED_NOTIFICATIONS", "ADD_ONE_NOTIFICATION"] as Mutations),
})
export default class AppComponent extends Vue {
    // State computed props
    usuarioLogado: UsuarioModel;
    logado: boolean;
    franquiaCidade: string;
    CLEAR_LISTSPACK: () => void;
    SET_UNREADED_NOTIFICATIONS: (value: number) => void;
    ADD_ONE_NOTIFICATION: () => void;

    private notificationService = new NotificationService();

    isNavMenuVisible = false;
    isMobile = false;

    private handleResize() {
        this.isMobile = window.matchMedia("(max-width: 767px)").matches;
    }

    private onCloseMenu() {
        Bus.$emit("hide-nav-menu");
    }

    private sessaoIntervaloTempo = 500; //consulta diferenca de tempo em x milissegundos
    private sessaoDiminuiIntervaloMinutos = 5; ///emite o aviso de x minutos a sessao expira e ja comeca controlar mais rapido o tempo
    private sessaoStartIntervalo = 60000; ///inicia buscando a cada um minuto e depois este tempo  vai diminuir quando ficar proximo
    private sessaoExpiracaoMinutos = 120; ///expira em x minutos
    private sessaoAvisoMinutos = 2; // aviso x minutos - expirarminutos antes de expirar
    private sessaoIntervaloID = 0;
    private sessaoUltimaAtividade; ///tempo salvo na ultima atividade
    private emitiuAvisoSessao = false;

    private get isNotProduction() {
        return (
            this.usuarioLogado.ambiente &&
            this.usuarioLogado.ambiente !== "Production" &&
            this.usuarioLogado.ambiente !== "Industria"
        );
    }

    private initSession() {
        this.sessaoUltimaAtividade = new Date().getTime();
        this.sessaoSetIntervalo();
    }

    private sessaoLimpaIntervalo() {
        clearInterval(this.sessaoIntervaloID);
    }

    private sessaoSetIntervalo(tempoMinimo = false) {
        this.emitiuAvisoSessao = tempoMinimo;
        this.sessaoLimpaIntervalo();
        this.sessaoIntervaloID = setInterval(
            () => this.sessaoIntervalo(),
            tempoMinimo ? this.sessaoIntervaloTempo : this.sessaoStartIntervalo,
        );
    }

    private sessaoRefresh() {
        this.sessaoUltimaAtividade = new Date().getTime();
        this.sessaoSetIntervalo();
    }

    private async sessaoLogOut() {
        this.sessaoLimpaIntervalo();

        await this.$store.dispatch(SessionActions.LOGOUT);
        this.$router.push("/login");
    }

    private async sessaoIntervalo() {
        const now = new Date().getTime();
        const diferencaMinutos = (now - this.sessaoUltimaAtividade) / 1000 / 60;

        const expirando = diferencaMinutos >= this.sessaoExpiracaoMinutos - this.sessaoAvisoMinutos;
        const avisoProximo = diferencaMinutos >= this.sessaoExpiracaoMinutos - this.sessaoDiminuiIntervaloMinutos;

        if (expirando) {
            this.sessaoLimpaIntervalo();
            await this.exibirAvisoSessaoExpirando();
        } else if (avisoProximo && !this.emitiuAvisoSessao) {
            this.$notifyInfo(
                `${this.$t("__.ts.sessaoExp")}${this.sessaoDiminuiIntervaloMinutos}${this.$t("__.ts.min")}`,
                this.$t("__.ts.tempoParaExp").toString(),
            );
            this.sessaoSetIntervalo(true);
        }
    }

    private async exibirAvisoSessaoExpirando() {
        const tempoAviso = this.sessaoAvisoMinutos * 60000;
        const html = `
            ${this.$t("__.ts.sessaoExp")}<strong>${this.sessaoAvisoMinutos * 60}</strong>${this.$t("__.ts.seg")}
            <div class="loader"></div><br/><br/>
            ${this.$t("__.ts.pressSimParaLogado")}
            ${this.$t("__.ts.pressNaoParaDesconect")}
        `;

        let timerInterval: ReturnType<typeof setInterval>;
        const timeInit = new Date().getTime();

        const response = await this.$showAlert({
            title: this.$t("__.ts.tempoParaExp") as string,
            html,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: this.$t("__.ts.sim") as string,
            cancelButtonText: this.$t("__.ts.nao") as string,
            allowOutsideClick: false,
            allowEscapeKey: false,
            timer: tempoAviso,
            didOpen: () => {
                timerInterval = setInterval(() => {
                    const secondsLeft = (tempoAviso - (new Date().getTime() - timeInit)) / 1000;
                    const element = Alert.getHtmlContainer().querySelector("strong");
                    if (element) {
                        element.textContent = secondsLeft.toFixed(0);
                    }
                }, 1000);
            },
            didClose: () => clearInterval(timerInterval),
        });

        if (response.isConfirmed) {
            this.CLEAR_LISTSPACK();
            this.initSession();
            this.sessaoUltimaAtividade = new Date().getTime();
        } else {
            await this.sessaoLogOut();
        }
    }

    private click() {
        Bus.$emit("hide-nav-menu");
    }

    private showKeyToolTip = false;

    private onKeyAlt() {
        this.showKeyToolTip = !this.showKeyToolTip;
        Bus.$emit("showShortKeyToolTip", this.showKeyToolTip);
    }

    private async mounted() {
        this.handleResize = this.handleResize.bind(this);
        this.handleResize();
        window.addEventListener("resize", this.handleResize);

        Bus.$on("nav-menu-change", (isVisible: boolean) => {
            this.isNavMenuVisible = isVisible;
        });

        //inicia controle de tempo de ssesao
        if (this.logado) {
            this.initSession();
        }

        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);

        // gerar o token para envio de msg pelo whats - precisa gerar mesmo que nao tenha modulo contratado
        await new ConfiguracaoWhatsAppService().getTokenAtendimento();
        // iniciar conexão websocket
        await new PharmUPHub(this.$router).criarConexaoWebSocketPharmUP();
        // iniciar conexão websocket de atendimento apenas quando a franquia tem o módulo contratado
        if (this.usuarioLogado.franquia.moduloWhatsApp) {
            await new MensagemAtendimentoHub().criarConexaoWebSocketMensagemAtendimento();
        }

        Bus.$on("add-one-notification", () => {
            this.ADD_ONE_NOTIFICATION();
        });

        Bus.$on("nfetch-service-event", () => {
            if (this.logado) {
                this.sessaoRefresh();
            }
        });

        Promise.all([this.CLEAR_LISTSPACK()])
            .then(() => {})
            .catch(() => {});

        this.SET_UNREADED_NOTIFICATIONS(await this.notificationService.countUnreaded().resolveWithJSON<number>());
    }

    private beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        Bus.$off("nav-menu-change");
        this.sessaoLimpaIntervalo();
    }
}
